<template>
	<div
		v-if="loading"
		class="loading-spinner-container"
	>
		<LoadingSpinner :is-overlay="true" />
	</div>
	<div v-else>
		<div class="journeys-skew-banner">
			<div class="container">
				<div class="row">
					<div class="col journeys-skew-banner__content text-center">
						<h1 class="h2">{{ microcopy?.heading }}</h1>
						<p>{{ microcopy?.summary }}</p>
					</div>
				</div>
			</div>
		</div>
		<div class="container pull-up">
			<form
				class="w-100"
				@submit.prevent
			>
				<div class="rounded-container rounded-container--narrow">
					<div class="row">
						<div class="col rounded-container__btn-col">
							<div class="">
								<IncorrectDetailsErrorComponent v-if="showError" />
								<TextInputField
									input-type="email"
									:label-text="microcopy?.emailInputLabel"
									:error-text="microcopy?.emailInputError"
									@field-input="(payload) => {
										inputValue.email = payload.value;
										isValid.email = payload.isValid;
									}"
								/>
								<TextInputField
									input-type="postcode"
									:label-text="microcopy?.homePostcodeInputLabel"
									:error-text="microcopy?.homePostcodeInputError"
									@field-input="(payload) => {
										inputValue.postcode = payload.value;
										isValid.postcode = payload.isValid;
									}"
								/>
								<NumericIndividualFields
									:label-text="microcopy?.dateOfBirthInputLabel"
									:error-text="microcopy?.dateOfBirthErrorLabel"
									@field-input="(payload) => {
										inputValue.dob = payload.value;
										isValid.dob = payload.isValid;
									}"
								/>
							</div>
						</div>
					</div>
				</div>
				<div class="row btm-btn-group m-auto mb-5">
					<div class="col text-center d-flex flex-column align-items-center">
						<GenericButton
							:label-text="microcopy?.nextButtonLabel"
							:button-disabled="buttonDisabled"
							@button-click="handleClick"
						/>
						<BackButton
							:back-text="microcopy?.backButtonLabel"
							@back-click="handleBackButton"
							style="margin-top: 32px;"
						/>
					</div>
				</div>
			</form>
		</div>
	</div>
</template>

<script>
import TextInputField from '@/shared/components/TextInputField.vue';
import GenericButton from '@/shared/components/GenericButton.vue';
import NumericIndividualFields from '@/shared/components/NumericIndividualFields.vue';
import BackButton from '@/shared/components/BackButton.vue';
import IncorrectDetailsErrorComponent from '../components/IncorrectDetailsErrorComponent.vue';
import LoadingSpinner from '@/shared/components/LoadingSpinner.vue';

import router from '../router/index.js';

import { mapActions } from 'pinia';
import { useAppointmentReferralStore } from '../stores/appointmentReferralStore.js';

export default {
	components: {
		TextInputField,
		GenericButton,
		NumericIndividualFields,
		BackButton,
		IncorrectDetailsErrorComponent,
		LoadingSpinner
	},
	data() {
		return {
			showError: false,
			inputValue: {
				email: '',
				postcode: '',
				dob: '',
			},
			isValid: {
				email: false,
				postcode: false,
				dob: false,
			},
			attempts: 0,
			microcopy: '',
			loading: false,
		};
	},
	computed: {
		allInputsValid() {
			return this.isValid.email && this.isValid.postcode && this.isValid.dob;
		},
		buttonDisabled() {
			return !this.allInputsValid || this.showError;
		}
	},
	watch: {
		inputValue: {
			handler() {
				this.showError = false;
			},
			deep: true
		},
		attempts(newValue) {
			if (newValue >= 3) {
				router.push('error')
			}
		}
	},
	mounted() {
		this.resetNewUserDetails();
		this.microcopy = window.bookAnAppointmentReferralMicrocopy?.existingCustomer;
		document.title = this.microcopy?.metaTitle
	},
	methods: {
		...mapActions(useAppointmentReferralStore, ['postExistingCustomerDetails', 'resetNewUserDetails']),
		handleClick() {
			this.existingCustomerLookup();
		},
		existingCustomerLookup() {
			this.loading = true;
			this.showError = false;

			appInsights.trackTrace({
				message: '[BAARefJourney] API existing customer lookup - Started',
				properties: { area: 'appointment-referral-journey', hostname: location.hostname },
				severityLevel: 1
			});

			const options = {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					emailAddress: this.inputValue.email,
					dateOfBirth: this.inputValue.dob,
					postcode: this.inputValue.postcode,
				}),
			};

			fetch("/sapi/FinancialConsultants/existingCustomerSearch", options)
				.then(async (res) => {
					if (!res.ok) {
						console.error(`Request failed with status ${res.status}`);
						appInsights.trackTrace({
							message: `[BAARefJourney] API existing customer lookup - Failed - received HTTP ${res.status}`,
							properties: { area: 'appointment-referral-journey', hostname: location.hostname },
							severityLevel: 4
						});

						if (res.status === 500) {
							return router.push('error');
						}

						this.attempts++;
						this.showError = true;
						return;
					}

					if (res.status === 204) {
						appInsights.trackTrace({
							message: '[BAARefJourney] API existing customer lookup - Succeeded - received HTTP 204',
							properties: { area: 'appointment-referral-journey', hostname: location.hostname },
							severityLevel: 1
						});
						return;
					}

					appInsights.trackTrace({
						message: '[BAARefJourney] API existing customer lookup - Succeeded',
						properties: { area: 'appointment-referral-journey', hostname: location.hostname },
						severityLevel: 1
					});

					const data = await res.text();
					this.postExistingCustomerDetails(this.inputValue, data);

					return router.push('your-financial-adviser');
				})
				.catch((e) => {
					console.error(e);
					appInsights.trackException({
						exception: e,
						properties: { area: 'appointment-referral-journey' },
						severityLevel: 4
					});
					return router.push('error');
				})
				.finally(() => {
					this.loading = false;
				});
		},
		handleBackButton() {
			router.back();
		},
	},
};
</script>
